import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spin, notification, Popconfirm } from "antd";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import styles from "./style.module.scss";

const ORG_STATUS_FILTERS = {
  active: "active",
  pending: "pending",
  in_progress: "in_progress",
  completed: "completed",
  failed: "failed",
};

function formatDuration(seconds) {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m} min ${rem} sec` : `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h} hr ${rm} min` : `${h} hr`;
}

function formatTimestamp(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale || undefined);
  } catch {
    return String(iso);
  }
}

function StatusBadge({ status, t }) {
  const label =
    status === "pending"
      ? t("genai_queue_page.statusPending")
      : status === "in_progress"
      ? t("genai_queue_page.statusInProgress")
      : status === "ready"
      ? t("genai_queue_page.statusCompleted")
      : status === "failed"
      ? t("genai_queue_page.statusFailed")
      : status;
  const cls =
    status === "pending"
      ? styles.statusPending
      : status === "in_progress"
      ? styles.statusProgress
      : status === "ready"
      ? styles.statusReady
      : styles.statusFailed;
  return <span className={`${styles.statusBadge} ${cls}`}>{label}</span>;
}

function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      className={styles.sortableTh}
      onClick={() => onSort(sortKey)}
      aria-sort={
        active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"
      }
    >
      {label}
      <span className={styles.sortIconWrap} aria-hidden>
        <CaretUpOutlined
          className={`${styles.sortIcon} ${
            active && sort.direction === "asc" ? styles.sortIconActive : ""
          }`}
        />
        <CaretDownOutlined
          className={`${styles.sortIcon} ${
            active && sort.direction === "desc" ? styles.sortIconActive : ""
          }`}
        />
      </span>
    </th>
  );
}

const ROW_SORT_GETTERS = {
  queue: (row) =>
    Number(
      row.organization_queue_position ?? row.global_queue_position ?? 999999,
    ),
  status: (row) => row.status || "",
  widget: (row) => row.widget_name || "",
  user: (row) => row.workspace_user_email || "",
  llm: (row) => row.llm_provider || "",
  widget_created_at: (row) =>
    row.widget_created_at || row.created_at || "",
  completed_at: (row) => row.completed_at || "",
  elapsed: (row) =>
    Number.isFinite(Number(row.elapsed_seconds))
      ? Number(row.elapsed_seconds)
      : 0,
  updated_at: (row) => row.updated_at || "",
};

function sortRows(rows, sort) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (!sort?.key || !ROW_SORT_GETTERS[sort.key]) return list;
  const getter = ROW_SORT_GETTERS[sort.key];
  const dir = sort.direction === "desc" ? -1 : 1;
  list.sort((a, b) => {
    const av = getter(a);
    const bv = getter(b);
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * dir;
  });
  return list;
}

const STALE_IN_PROGRESS_SECONDS = 6 * 3600;

function isStaleInProgressRow(row) {
  if (row?.status !== "in_progress") return false;
  const elapsed = Number(row.elapsed_seconds);
  return Number.isFinite(elapsed) && elapsed >= STALE_IN_PROGRESS_SECONDS;
}

function canRemoveQueueRow(row) {
  return (
    row?.is_organization &&
    (row.status === "pending" ||
      row.status === "failed" ||
      isStaleInProgressRow(row))
  );
}

function pruneRemovedJobFromQueueData(prev, jobId) {
  if (!prev || jobId == null) return prev;
  const id = Number(jobId);
  const filterJob = (r) => Number(r.job_id) !== id;
  const findRemoved = (list) =>
    (list || []).find((r) => Number(r.job_id) === id);
  const removed =
    findRemoved(prev.active_queue) ||
    findRemoved(prev.recent_failed) ||
    findRemoved(prev.recent_completed);

  const next = {
    ...prev,
    active_queue: (prev.active_queue || []).filter(filterJob),
    recent_failed: (prev.recent_failed || []).filter(filterJob),
    recent_completed: (prev.recent_completed || []).filter(filterJob),
  };

  if (!removed || !next.summary) return next;

  const summary = {
    ...next.summary,
    organization: { ...(next.summary.organization || {}) },
    global: { ...(next.summary.global || {}) },
  };
  const decBucket = (scope, bucket) => {
    if (summary[scope]?.[bucket] > 0) {
      summary[scope][bucket] -= 1;
    }
  };
  const bucketForStatus =
    removed.status === "pending"
      ? "pending"
      : removed.status === "in_progress"
      ? "in_progress"
      : removed.status === "failed"
      ? "failed_24h"
      : removed.status === "ready"
      ? "completed_24h"
      : null;
  if (bucketForStatus) {
    decBucket("global", bucketForStatus);
    if (removed.is_organization) {
      decBucket("organization", bucketForStatus);
    }
  }
  next.summary = summary;
  return next;
}

function mergeQueueFetchWithSuppression(serverData, suppressedIds) {
  if (!serverData) return serverData;
  if (!suppressedIds?.size) return serverData;

  const serverActiveIds = new Set(
    (serverData.active_queue || []).map((r) => Number(r.job_id)),
  );
  suppressedIds.forEach((id) => {
    if (!serverActiveIds.has(id)) {
      suppressedIds.delete(id);
    }
  });

  if (!suppressedIds.size) return serverData;

  const filterJob = (r) => !suppressedIds.has(Number(r.job_id));
  return {
    ...serverData,
    active_queue: (serverData.active_queue || []).filter(filterJob),
    recent_failed: (serverData.recent_failed || []).filter(filterJob),
    recent_completed: (serverData.recent_completed || []).filter(filterJob),
  };
}

function QueueTable({
  rows,
  t,
  locale,
  showQueuePosition = true,
  onRemove,
  removingJobId,
  emptyMessageKey = "genai_queue_page.emptyQueue",
}) {
  const [sort, setSort] = useState({ key: null, direction: "asc" });

  const onSort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
  }, []);

  const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort]);

  if (!sortedRows?.length) {
    return (
      <div className={styles.emptyState}>{t(emptyMessageKey)}</div>
    );
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {showQueuePosition ? (
            <SortHeader
              label={t("genai_queue_page.colQueue")}
              sortKey="queue"
              sort={sort}
              onSort={onSort}
            />
          ) : null}
          <SortHeader
            label={t("genai_queue_page.colStatus")}
            sortKey="status"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colWidget")}
            sortKey="widget"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colUser")}
            sortKey="user"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colLlm")}
            sortKey="llm"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colWidgetCreated")}
            sortKey="widget_created_at"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colCompleted")}
            sortKey="completed_at"
            sort={sort}
            onSort={onSort}
          />
          <SortHeader
            label={t("genai_queue_page.colElapsed")}
            sortKey="elapsed"
            sort={sort}
            onSort={onSort}
          />
          {onRemove ? (
            <th>{t("genai_queue_page.colActions")}</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row) => {
          const queuePos =
            row.organization_queue_position ?? row.global_queue_position;
          return (
            <tr key={`${row.job_id}-${row.widget_id}`}>
              {showQueuePosition ? (
                <td>
                  {queuePos != null ? (
                    <>
                      <strong>#{queuePos}</strong>
                      {row.global_queue_position != null &&
                      row.organization_queue_position == null ? (
                        <span className={styles.cellMuted}>
                          {" "}
                          ({t("genai_queue_page.globalOnly")})
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
              ) : null}
              <td>
                <StatusBadge status={row.status} t={t} />
              </td>
              <td>
                <div className={styles.widgetName}>{row.widget_name}</div>
                <div className={styles.groupName}>
                  {row.group_name}
                  {row.is_auto_report ? (
                    <span className={styles.orgBadge}>
                      {t("genai_queue_page.autoReport")}
                    </span>
                  ) : null}
                </div>
                <div className={styles.cellMuted}>{row.metric_name}</div>
              </td>
              <td>
                {row.is_auto_report && row.report_created_by_email ? (
                  <>
                    <div>{row.report_created_by_email}</div>
                    <div className={styles.cellMuted}>
                      {t("genai_queue_page.reportCreatedBy")}
                    </div>
                    {row.report_created_by_email !== row.workspace_user_email ? (
                      <div className={styles.cellMuted}>
                        {t("genai_queue_page.workspaceOwner", {
                          email: row.workspace_user_email,
                        })}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div>{row.workspace_user_email}</div>
                )}
                <div className={styles.cellMuted}>
                  widget #{row.widget_id} · job #{row.job_id}
                </div>
              </td>
              <td>
                <div>{row.llm_provider || "—"}</div>
                <div className={styles.cellMuted}>{row.llm_model || ""}</div>
              </td>
              <td>
                {formatTimestamp(
                  row.widget_created_at || row.created_at,
                  locale,
                )}
              </td>
              <td>{formatTimestamp(row.completed_at, locale)}</td>
              <td>
                {row.elapsed_seconds != null
                  ? formatDuration(row.elapsed_seconds)
                  : row.updated_at
                  ? formatTimestamp(row.updated_at, locale)
                  : "—"}
                {row.status_description ? (
                  <div
                    className={
                      row.status === "failed"
                        ? styles.errorDetail
                        : styles.cellMuted
                    }
                  >
                    {row.status_description}
                  </div>
                ) : null}
              </td>
              {onRemove ? (
                <td>
                  {canRemoveQueueRow(row) ? (
                    <Popconfirm
                      title={t("genai_queue_page.removeConfirmTitle")}
                      description={
                        row.is_auto_report
                          ? t("genai_queue_page.removeConfirmAutoReport")
                          : isStaleInProgressRow(row)
                          ? t("genai_queue_page.removeConfirmStale")
                          : t("genai_queue_page.removeConfirmBody")
                      }
                      okText={t("genai_queue_page.remove")}
                      cancelText={t("common.cancel")}
                      onConfirm={() => onRemove(row)}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={removingJobId === row.job_id}
                        aria-label={t("genai_queue_page.remove")}
                      />
                    </Popconfirm>
                  ) : row.is_organization &&
                    row.status === "in_progress" &&
                    !isStaleInProgressRow(row) ? (
                    <span className={styles.cellMuted}>
                      {t("genai_queue_page.removeInProgress")}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function formatUsd(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function formatProviderLabel(provider) {
  if (!provider) return "";
  const key = String(provider).toLowerCase();
  if (key === "vertex") return "Vertex AI";
  if (key === "deepseek") return "DeepSeek";
  return String(provider);
}

function SpendSummaryPanel({ summary, t }) {
  const balance = summary?.deepseek_balance;
  const spend = summary?.auto_report_spend;
  const jobActivity = summary?.genai_job_activity?.organization || {
    last_24h: {
      completed: summary?.organization?.completed_24h ?? 0,
      failed: summary?.organization?.failed_24h ?? 0,
    },
    last_7d: { completed: 0, failed: 0 },
    all_time: { completed: 0, failed: 0 },
  };
  const platformLlm = spend?.platform_llm_provider || summary?.platform_llm_provider;
  if (!balance && !spend) return null;

  const balanceTone =
    balance?.status === "ok"
      ? styles.balanceOk
      : balance?.status === "low"
      ? styles.balanceLow
      : styles.balanceBad;

  const rows = [
    { key: "last_24h", label: t("genai_queue_page.spendWindow24h") },
    { key: "last_7d", label: t("genai_queue_page.spendWindow7d") },
    { key: "all_time", label: t("genai_queue_page.spendWindowAll") },
  ];

  const spendHintKey =
    platformLlm === "vertex"
      ? "genai_queue_page.spendHintVertex"
      : "genai_queue_page.spendHintDeepseek";

  return (
    <div className={styles.spendPanel}>
      {balance ? (
        <div className={`${styles.balanceCard} ${balanceTone}`}>
          <span className={styles.balanceLabel}>
            {t("genai_queue_page.deepseekBalance")}
          </span>
          <span className={styles.balanceValue}>
            {balance.ok && balance.total_balance != null
              ? `${Number(balance.total_balance).toFixed(2)} ${balance.currency || "USD"}`
              : t("genai_queue_page.deepseekBalanceUnavailable")}
          </span>
          {balance.ok && balance.is_available === false ? (
            <span className={styles.balanceHint}>
              {t("genai_queue_page.deepseekBalanceDepleted")}
            </span>
          ) : null}
        </div>
      ) : null}

      {spend ? (
        <div className={styles.spendTableWrap}>
          <p className={styles.spendTitle}>{t("genai_queue_page.spendTitle")}</p>
          <p className={styles.spendHint}>{t(spendHintKey)}</p>
          <table className={styles.spendTable}>
            <thead>
              <tr>
                <th>{t("genai_queue_page.spendColPeriod")}</th>
                <th>{t("genai_queue_page.spendColJobsCompleted")}</th>
                <th>{t("genai_queue_page.spendColJobsFailed")}</th>
                <th>{t("genai_queue_page.spendColReportCount")}</th>
                <th>{t("genai_queue_page.spendColTotal")}</th>
                <th>{t("genai_queue_page.spendColAvgPerReport")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, label }) => {
                const window = spend[key] || {};
                const jobs = jobActivity[key] || {};
                return (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>{jobs.completed ?? 0}</td>
                    <td>{jobs.failed ?? 0}</td>
                    <td>{window.report_count ?? 0}</td>
                    <td>{formatUsd(window.total_usd)}</td>
                    <td>{formatUsd(window.avg_usd_per_report)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const Representation = ({ user, locale, theme = "dark" }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [data, setData] = useState(null);
  const [orgStatusFilter, setOrgStatusFilter] = useState(
    ORG_STATUS_FILTERS.active,
  );
  const [removingJobId, setRemovingJobId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const suppressedJobIdsRef = useRef(new Set());

  const fetchQueue = useCallback(async (options = {}) => {
    const { clearSuppressed = false, silent = false } = options;
    if (clearSuppressed) {
      suppressedJobIdsRef.current.clear();
    }
    if (!user?.uid) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (!silent) {
      setRefreshing(true);
    }
    try {
      const result = await apiRequest(
        options?.refreshBalance
          ? "welcome/power-user/genai-queue?refresh_balance=1"
          : "welcome/power-user/genai-queue",
        "GET",
        null,
        true,
      );
      if (result?.error) {
        if (
          String(result.error).toLowerCase().includes("forbidden") ||
          result.error === "FORBIDDEN"
        ) {
          setForbidden(true);
        } else {
          notification.error({
            message: t("common.error"),
            description: result.error,
          });
        }
        return;
      }
      if (result?.success) {
        setData(
          mergeQueueFetchWithSuppression(
            result.success,
            suppressedJobIdsRef.current,
          ),
        );
        setForbidden(false);
      }
    } catch (err) {
      notification.error({
        message: t("common.error"),
        description: err?.message || String(err),
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.uid, t]);

  useEffect(() => {
    fetchQueue({ silent: true });
    const id = setInterval(() => fetchQueue({ silent: true }), 10000);
    return () => clearInterval(id);
  }, [fetchQueue]);

  const handleRemoveJob = useCallback(
    async (row) => {
      if (!row?.job_id) return;
      setRemovingJobId(row.job_id);
      try {
        const result = await apiRequest(
          "welcome/power-user/genai-queue/remove",
          "POST",
          { job_id: row.job_id },
          true,
        );
        if (result?.error) {
          notification.error({
            message: t("common.error"),
            description: result.error,
          });
          return;
        }
        if (!result?.success) {
          notification.error({
            message: t("common.error"),
            description: t("genai_queue_page.removeFailed"),
          });
          return;
        }

        const removedJobId = Number(
          result.success?.removed_job_id ?? row.job_id,
        );
        suppressedJobIdsRef.current.add(removedJobId);
        setData((prev) => pruneRemovedJobFromQueueData(prev, removedJobId));

        const remainingSimilar = (data?.active_queue || []).filter(
          (r) =>
            Number(r.job_id) !== removedJobId &&
            r.widget_name === row.widget_name &&
            r.group_name === row.group_name &&
            r.metric_name === row.metric_name,
        ).length;

        notification.success({
          message: t("genai_queue_page.removeSuccess"),
          description:
            remainingSimilar > 0
              ? t("genai_queue_page.removeSuccessDuplicates", {
                  count: remainingSimilar,
                })
              : undefined,
        });
      } catch (err) {
        notification.error({
          message: t("common.error"),
          description: err?.message || String(err),
        });
      } finally {
        setRemovingJobId(null);
      }
    },
    [fetchQueue, t, data],
  );

  const summary = data?.summary;
  const org = summary?.organization || {};
  const global = summary?.global || {};
  const activeQueue = data?.active_queue || [];

  const orgActive = useMemo(
    () => activeQueue.filter((r) => r.is_organization),
    [activeQueue],
  );
  const globalActive = activeQueue;

  const orgFilteredRows = useMemo(() => {
    const completed = data?.recent_completed || [];
    const failed = data?.recent_failed || [];
    switch (orgStatusFilter) {
      case ORG_STATUS_FILTERS.pending:
        return orgActive.filter((r) => r.status === "pending");
      case ORG_STATUS_FILTERS.in_progress:
        return orgActive.filter((r) => r.status === "in_progress");
      case ORG_STATUS_FILTERS.completed:
        return completed;
      case ORG_STATUS_FILTERS.failed:
        return failed;
      case ORG_STATUS_FILTERS.active:
      default:
        return orgActive;
    }
  }, [orgActive, data?.recent_completed, data?.recent_failed, orgStatusFilter]);

  const orgSectionTitle = useMemo(() => {
    switch (orgStatusFilter) {
      case ORG_STATUS_FILTERS.pending:
        return t("genai_queue_page.sectionOrgPending");
      case ORG_STATUS_FILTERS.in_progress:
        return t("genai_queue_page.sectionOrgInProgress");
      case ORG_STATUS_FILTERS.completed:
        return t("genai_queue_page.sectionOrgCompleted");
      case ORG_STATUS_FILTERS.failed:
        return t("genai_queue_page.sectionOrgFailed");
      default:
        return t("genai_queue_page.sectionOrgQueue");
    }
  }, [orgStatusFilter, t]);

  const showOrgQueuePosition =
    orgStatusFilter === ORG_STATUS_FILTERS.active ||
    orgStatusFilter === ORG_STATUS_FILTERS.pending;

  const toggleOrgFilter = useCallback((filterKey) => {
    setOrgStatusFilter((prev) =>
      prev === filterKey ? ORG_STATUS_FILTERS.active : filterKey,
    );
  }, []);

  const orgEmptyMessageKey = useMemo(() => {
    switch (orgStatusFilter) {
      case ORG_STATUS_FILTERS.completed:
        return "genai_queue_page.emptyCompleted24h";
      case ORG_STATUS_FILTERS.failed:
        return "genai_queue_page.emptyFailed24h";
      case ORG_STATUS_FILTERS.pending:
        return "genai_queue_page.emptyPending";
      case ORG_STATUS_FILTERS.in_progress:
        return "genai_queue_page.emptyInProgress";
      default:
        return "genai_queue_page.emptyQueue";
    }
  }, [orgStatusFilter]);

  const platformProvider = summary?.platform_llm_provider;
  const providerLabel = formatProviderLabel(platformProvider);
  const failedPreview =
    summary?.recent_failed_preview?.length > 0
      ? summary.recent_failed_preview
      : (data?.recent_failed || []).slice(0, 3);
  const showFailedAlert =
    (org.failed_24h ?? 0) > 0 &&
    orgStatusFilter !== ORG_STATUS_FILTERS.failed;

  if (forbidden) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.forbidden}>{t("genai_queue_page.forbidden")}</p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.breadcrumb}>{t("genai_queue_page.breadcrumb")}</p>
      <h1 className={styles.title}>
        {providerLabel
          ? t("genai_queue_page.titleWithProvider", {
              provider: providerLabel,
            })
          : t("genai_queue_page.title")}
      </h1>
      <p className={styles.subtitle}>{t("genai_queue_page.subtitle")}</p>
      <p className={styles.scopeNote}>{t("genai_queue_page.scopeNote")}</p>

      <div className={styles.toolbar}>
        <div>
          {summary?.platform_llm_provider ? (
            <span className={styles.metaChip}>
              {t("genai_queue_page.platformLlm", {
                provider: summary.platform_llm_provider,
              })}
            </span>
          ) : null}
          {summary?.organization_member_count != null ? (
            <span className={styles.metaChip} style={{ marginLeft: 8 }}>
              {t("genai_queue_page.memberCount", {
                count: summary.organization_member_count,
              })}
            </span>
          ) : null}
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => fetchQueue({ clearSuppressed: true, refreshBalance: true })}
        >
          {t("genai_queue_page.refresh")}
        </Button>
      </div>

      <SpendSummaryPanel summary={summary} t={t} />

      {showFailedAlert ? (
        <div className={styles.failedAlert} role="alert">
          <div className={styles.failedAlertBody}>
            <strong>{t("genai_queue_page.failedAlertTitle", { count: org.failed_24h })}</strong>
            <p>{t("genai_queue_page.failedAlertBody")}</p>
            {failedPreview.length ? (
              <ul className={styles.failedPreviewList}>
                {failedPreview.map((row) => (
                  <li key={row.job_id}>
                    {row.widget_name} · {row.group_name}
                    {row.report_created_by_email
                      ? ` · ${row.report_created_by_email}`
                      : row.workspace_user_email
                      ? ` · ${row.workspace_user_email}`
                      : ""}
                    {row.status_description ? ` — ${row.status_description}` : ""}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <Button type="primary" danger onClick={() => setOrgStatusFilter(ORG_STATUS_FILTERS.failed)}>
            {t("genai_queue_page.failedAlertAction")}
          </Button>
        </div>
      ) : null}

      {loading && !data ? (
        <Spin />
      ) : (
        <>
          <div className={styles.statsRow}>
            <button
              type="button"
              className={`${styles.statCard} ${
                orgStatusFilter === ORG_STATUS_FILTERS.pending
                  ? styles.statCardActive
                  : ""
              }`}
              onClick={() => toggleOrgFilter(ORG_STATUS_FILTERS.pending)}
            >
              <span className={styles.statValue}>{org.pending ?? 0}</span>
              <span className={styles.statLabel}>
                {t("genai_queue_page.statOrgPending")}
              </span>
              <span className={styles.statSub}>
                {t("genai_queue_page.statGlobalPending", {
                  count: global.pending ?? 0,
                })}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.statCard} ${
                orgStatusFilter === ORG_STATUS_FILTERS.in_progress
                  ? styles.statCardActive
                  : ""
              }`}
              onClick={() => toggleOrgFilter(ORG_STATUS_FILTERS.in_progress)}
            >
              <span className={styles.statValue}>{org.in_progress ?? 0}</span>
              <span className={styles.statLabel}>
                {t("genai_queue_page.statOrgInProgress")}
              </span>
              <span className={styles.statSub}>
                {t("genai_queue_page.statGlobalInProgress", {
                  count: global.in_progress ?? 0,
                })}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.statCard} ${
                orgStatusFilter === ORG_STATUS_FILTERS.completed
                  ? styles.statCardActive
                  : ""
              }`}
              onClick={() => toggleOrgFilter(ORG_STATUS_FILTERS.completed)}
            >
              <span className={styles.statValue}>
                {org.completed_24h ?? 0}
              </span>
              <span className={styles.statLabel}>
                {t("genai_queue_page.statOrgCompleted24h")}
              </span>
              <span className={styles.statSub}>
                {t("genai_queue_page.statGlobalCompleted24h", {
                  count: global.completed_24h ?? 0,
                })}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.statCard} ${
                orgStatusFilter === ORG_STATUS_FILTERS.failed
                  ? styles.statCardActive
                  : ""
              }`}
              onClick={() => toggleOrgFilter(ORG_STATUS_FILTERS.failed)}
            >
              <span className={styles.statValue}>{org.failed_24h ?? 0}</span>
              <span className={styles.statLabel}>
                {t("genai_queue_page.statOrgFailed24h")}
              </span>
              <span className={styles.statSub}>
                {t("genai_queue_page.statGlobalFailed24h", {
                  count: global.failed_24h ?? 0,
                })}
              </span>
            </button>
          </div>
          <p className={styles.statsHint}>{t("genai_queue_page.statsHint")}</p>

          <h2 className={styles.sectionTitle}>{orgSectionTitle}</h2>
          <div className={styles.tableWrap}>
            <QueueTable
              rows={orgFilteredRows}
              t={t}
              locale={locale}
              showQueuePosition={showOrgQueuePosition}
              onRemove={handleRemoveJob}
              removingJobId={removingJobId}
              emptyMessageKey={orgEmptyMessageKey}
            />
          </div>

          <h2 className={styles.sectionTitle}>
            {t("genai_queue_page.sectionGlobalQueue")}
          </h2>
          <div className={styles.tableWrap}>
            <QueueTable
              rows={globalActive}
              t={t}
              locale={locale}
              showQueuePosition
              onRemove={handleRemoveJob}
              removingJobId={removingJobId}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Representation;
